import React, {
  useEffect,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useOffice } from "@/context/OfficeContext";

import {
  LayoutDashboard,
  BookOpen,
  FileText,
  Wallet,
  ReceiptText,
  Boxes,
  Building2,
  Coins,
  Settings,
  ShieldCheck,
  ScrollText,
  Landmark,
  Calculator,
  BarChart3,
  Warehouse,
  Repeat2,
  History,
  ShoppingCart,
  PackageSearch,
  ClipboardList,
  Truck,
  Target,
  Sparkles,
  Layers,
  FileSpreadsheet,
  Mail,
  MessageSquare,
  CalendarDays,
  Contact,
  Megaphone,
  BriefcaseBusiness,
  UserRound,
  HeartPulse,
  GraduationCap,
  PiggyBank,
  Smartphone,
  Users,
  Percent,
  ShieldAlert,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { isMicrofinanceWorkspace, isRouteAllowed } from "@/lib/routeAccess";


/*
 * Navigation access rules.
 *
 * Access rules live in /lib/routeAccess.js so the sidebar
 * and RoleGuard cannot diverge.
 *
 * Anything omitted here is still access-controlled at the
 * route level; it simply will not appear in the sidebar
 * for hidden roles.
 */
const CORE_DENY = [
  "hr_clerk",
  "sales_rep",
  "management",
  "viewer",
];

const SALES_ALLOWED_DENY = [
  "hr_clerk",
];

const NO_SALES_DENY = [
  "hr_clerk",
  "sales_rep",
];

const SALES_RESTRICTED_DENY = [
  "sales_rep",
];


const GROUPS = [
  {
    label: "Overview",

    items: [
      {
        to: "/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        testId: "nav-dashboard",
        denyRoles: SALES_RESTRICTED_DENY,
      },

      {
        to: "/boardroom",
        label: "Management Report",
        icon: Building2,
        testId: "nav-boardroom",
        roles: [
          "tenant_admin",
          "finance_manager",
          "financial_controller",
          "chief_finance_officer",
          "management",
          "accountant",
          "auditor",
          "viewer",
          "super_admin",
        ],
      },

      {
        to: "/analytics",
        label: "Analytics (BI)",
        icon: BarChart3,
        testId: "nav-analytics",
        roles: [
          "tenant_admin",
          "finance_manager",
          "financial_controller",
          "chief_finance_officer",
          "management",
          "accountant",
          "auditor",
          "viewer",
          "super_admin",
        ],
      },
    ],
  },


  {
    label: "Reports",

    items: [
      {
        to: "/reports-center",
        label: "Reports Center",
        icon: BarChart3,
        testId: "nav-reports-center",
        roles: [
          "tenant_admin",
          "finance_manager",
          "management",
          "accountant",
          "auditor",
          "viewer",
          "super_admin",
        ],
      },
    ],
  },


  {
    label: "Core Accounting",

    items: [
      {
        to: "/accounts",
        label: "Chart of Accounts",
        icon: BookOpen,
        testId: "nav-accounts",
        denyRoles: CORE_DENY,
      },

      {
        to: "/journals",
        label: "Journal Entries",
        icon: FileText,
        testId: "nav-journals",
        denyRoles: CORE_DENY,
      },

      {
        to: "/ledger",
        label: "General Ledger",
        icon: ScrollText,
        testId: "nav-ledger",
        denyRoles: CORE_DENY,
      },

      {
        to: "/cashbook",
        label: "Cashbook",
        icon: Wallet,
        testId: "nav-cashbook",
        denyRoles: CORE_DENY,
      },

      {
        to: "/reports",
        label: "Financial Reports",
        icon: BarChart3,
        testId: "nav-reports",
        denyRoles: CORE_DENY,
      },

      {
        to: "/budgeting",
        label: "Budgeting",
        icon: Target,
        testId: "nav-budgeting",
        denyRoles: CORE_DENY,
      },
    ],
  },


  {
    label: "Operations",

    items: [
      {
        to: "/pos",
        label: "Product Sales POS",
        icon: ShoppingCart,
        testId: "nav-pos",
        subcategory: "Sales & Customers",
        denyRoles: SALES_ALLOWED_DENY,
      },

      {
        to: "/services-pos",
        label: "Services Sales POS",
        icon: Sparkles,
        testId: "nav-services-pos",
        subcategory: "Sales & Customers",
        denyRoles: SALES_ALLOWED_DENY,
      },

      {
        to: "/sales-history",
        label: "Sales History (Records)",
        icon: History,
        testId: "nav-sales-history",
        subcategory: "Sales & Customers",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/invoicing",
        label: "Sales/Services Invoicing (AR)",
        icon: ReceiptText,
        testId: "nav-invoicing",
        subcategory: "Sales & Customers",
        denyRoles: SALES_RESTRICTED_DENY,
      },

      {
        to: "/suppliers",
        label: "Customers & Suppliers",
        icon: Truck,
        testId: "nav-suppliers",
        subcategory: "Sales & Customers",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/payables",
        label: "Accounts Payable (AP)",
        icon: Landmark,
        testId: "nav-payables",
        subcategory: "Purchasing & Payables",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/purchase-orders",
        label: "Purchase Orders (Restocking)",
        icon: ClipboardList,
        testId: "nav-purchase-orders",
        subcategory: "Purchasing & Payables",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/inventory",
        label: "Stock Inventory",
        icon: Boxes,
        testId: "nav-inventory",
        subcategory: "Inventory & Production",
        denyRoles: SALES_ALLOWED_DENY,
      },

      {
        to: "/inventory-movements",
        label: "Stock Movements",
        icon: PackageSearch,
        testId: "nav-inv-movements",
        subcategory: "Inventory & Production",
        denyRoles: SALES_ALLOWED_DENY,
      },

      {
        to: "/productions",
        label: "Raw Materials & Production",
        icon: Warehouse,
        testId: "nav-productions",
        subcategory: "Inventory & Production",
      },

      {
        to: "/petty-cash",
        label: "Petty Cash (Daily Expenses)",
        icon: Coins,
        testId: "nav-petty-cash",
        subcategory: "Cash & Financial Assets",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/banks",
        label: "Bank Accounts",
        icon: Landmark,
        testId: "nav-bank-accounts",
        subcategory: "Cash & Financial Assets",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/bank-recon",
        label: "Bank Reconciliation",
        icon: Repeat2,
        testId: "nav-bank-recon",
        subcategory: "Cash & Financial Assets",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/fixed-assets",
        label: "Fixed Assets Register",
        icon: Warehouse,
        testId: "nav-fixed-assets",
        subcategory: "Cash & Financial Assets",
        denyRoles: NO_SALES_DENY,
      },
    ],
  },


  {
    label: "HR & Payroll",

    items: [
      {
        to: "/payroll",
        label: "Employee & Payroll",
        icon: Calculator,
        testId: "nav-payroll",

        roles: [
          "tenant_admin",
          "finance_manager",
          "financial_controller",
          "chief_finance_officer",
          "hr_clerk",
          "management",
          "auditor",
          "super_admin",
        ],
      },
    ],
  },

  {
    label: "Specialized Modules",
    items: [
      {
        to: "/pharmacy",
        label: "Pharmacy",
        icon: PackageSearch,
        testId: "nav-pharmacy",
        children: [
          { to: "/pharmacy/desk", label: "My Desk", icon: UserRound, testId: "nav-pharmacy-desk" },
          { to: "/pharmacy/pos", label: "Retail POS", icon: ShoppingCart, testId: "nav-pharmacy-pos" },
          { to: "/pharmacy/products", label: "Products", icon: Boxes, testId: "nav-pharmacy-products" },
          { to: "/pharmacy/procurement", label: "Procurement", icon: Truck, testId: "nav-pharmacy-procurement" },
          { to: "/pharmacy/receipts", label: "Receipts", icon: ClipboardList, testId: "nav-pharmacy-receipts" },
          { to: "/pharmacy/adjustments", label: "Adjustments", icon: Layers, testId: "nav-pharmacy-adjustments" },
          { to: "/pharmacy/movements", label: "Movement History", icon: History, testId: "nav-pharmacy-movements" },
        ],
      },
      {
        to: "/hospital",
        label: "Hospital Management",
        icon: HeartPulse,
        testId: "nav-hospital",
        children: [
          { to: "/hospital/desk", label: "My Desk", icon: UserRound, testId: "nav-hospital-desk" },
          { to: "/hospital", label: "Home", icon: HeartPulse, testId: "nav-hospital-home" },
          { to: "/hospital/schedule", label: "Scheduling & Wards", icon: HeartPulse, testId: "nav-hospital-schedule" },
          { to: "/hospital/triage", label: "Patient Triage", icon: HeartPulse, testId: "nav-hospital-triage" },
          { to: "/hospital/consultations", label: "Consultations", icon: HeartPulse, testId: "nav-hospital-consultations" },
          { to: "/hospital/diagnostics", label: "Lab & Imaging Worklist", icon: ClipboardList, testId: "nav-hospital-worklist" },
          { to: "/hospital/referrals", label: "Referrals", icon: ClipboardList, testId: "nav-hospital-referral-board" },
          { to: "/hospital/follow-ups", label: "Follow-ups", icon: CalendarDays, testId: "nav-hospital-followups" },
          { to: "/hospital/ward-care", label: "Ward Care", icon: HeartPulse, testId: "nav-hospital-ward-care" },
          { to: "/hospital/units/referrals", label: "Specialty Referrals", icon: ClipboardList, testId: "nav-hospital-referrals" },
          { to: "/hospital/units/diagnostics", label: "Laboratory & Radiology", icon: ClipboardList, testId: "nav-hospital-diagnostics" },
          { to: "/hospital/units/maternal-child", label: "Maternal & Child Health", icon: HeartPulse, testId: "nav-hospital-maternal-child" },
          { to: "/hospital/units/theatre", label: "Surgery & Theatre", icon: ClipboardList, testId: "nav-hospital-theatre" },
          { to: "/hospital/services", label: "Service Desk", icon: HeartPulse, testId: "nav-hospital-services" },
          { to: "/hospital/pharmacy", label: "Pharmacy", icon: PackageSearch, testId: "nav-hospital-pharmacy" },
          { to: "/hospital/pharmacy/inventory", label: "Pharmacy Stock Inventory", icon: Boxes, testId: "nav-hospital-pharmacy-inventory" },
          { to: "/hospital/billing", label: "Patient Billing", icon: ReceiptText, testId: "nav-hospital-billing" },
          { to: "/hospital/units/claims", label: "NHIS Claims & Billing", icon: ReceiptText, testId: "nav-hospital-claims" },
          { to: "/hospital/records", label: "Medical Records", icon: FileText, testId: "nav-hospital-records" },
          { to: "/hospital/units/inpatient", label: "Inpatient Wards & Discharge", icon: Building2, testId: "nav-hospital-inpatient" },
          { to: "/hospital/settings", label: "Clinical Settings", icon: Settings, testId: "nav-hospital-settings" },
        ],
      },
      {
        to: "/school",
        label: "School Management",
        icon: GraduationCap,
        testId: "nav-school",
        children: [
          { to: "/school", label: "Overview", icon: GraduationCap, testId: "nav-school-home" },
          { to: "/school/admissions", label: "Students & Admissions", icon: UserRound, testId: "nav-school-admissions" },
          { to: "/school/applications", label: "Online Applications", icon: ClipboardList, testId: "nav-school-applications" },
          { to: "/school/staff", label: "Staff", icon: BriefcaseBusiness, testId: "nav-school-staff" },
          { to: "/school/academics", label: "Academics", icon: BookOpen, testId: "nav-school-academics" },
          { to: "/school/timetable", label: "Timetable", icon: CalendarDays, testId: "nav-school-timetable" },
          { to: "/school/attendance", label: "Attendance", icon: ClipboardList, testId: "nav-school-attendance" },
          { to: "/school/assessments", label: "Grading & Report Cards", icon: FileSpreadsheet, testId: "nav-school-assessments" },
          { to: "/school/courses", label: "Course Pages", icon: BookOpen, testId: "nav-school-courses" },
          { to: "/school/calendar", label: "Events Calendar", icon: CalendarDays, testId: "nav-school-calendar" },
          { to: "/school/fees", label: "Fees & Billing", icon: Coins, testId: "nav-school-fees" },
          { to: "/school/notifications", label: "Notifications", icon: Megaphone, testId: "nav-school-notifications" },
          { to: "/school/website", label: "School Website", icon: Sparkles, testId: "nav-school-website" },
          { to: "/school/settings", label: "Settings", icon: Settings, testId: "nav-school-settings" },
        ],
      },
      {
        to: "/microfinance",
        label: "Microfinance Operations",
        icon: PiggyBank,
        testId: "nav-microfinance",
        children: [
          { to: "/microfinance", label: "Overview", icon: PiggyBank, testId: "nav-mfi-home" },
          { to: "/microfinance/clients", label: "Clients", icon: Users, testId: "nav-mfi-clients" },
          { to: "/microfinance/loans", label: "Loans & Credit", icon: Coins, testId: "nav-mfi-loans" },
          { to: "/field", label: "Field Officer App", icon: Smartphone, testId: "nav-mfi-field-app" },
          { to: "/microfinance/field-ops", label: "Field Ops: Officers & Routes", icon: Truck, testId: "nav-mfi-field-ops" },
          { to: "/microfinance/approvals", label: "Approvals & Sync Exceptions", icon: ShieldAlert, testId: "nav-mfi-approvals" },
          { to: "/microfinance/audit", label: "Audit Trail", icon: ScrollText, testId: "nav-mfi-audit" },
          { to: "/microfinance/settings", label: "Settings & Upgrades", icon: Settings, testId: "nav-mfi-settings" },
        ],
      },
    ],
  },


  {
    label: "Office",

    items: [
      { to: "/office/jobs", label: "Job Center", icon: BriefcaseBusiness, testId: "nav-office-jobs" },
      { to: "/office/people", label: "People", icon: UserRound, testId: "nav-office-people" },
      { to: "/office/documents", label: "Documents & Sheets", icon: FileSpreadsheet, testId: "nav-office-documents" },
      { to: "/office/mail", label: "Mail", icon: Mail, testId: "nav-office-mail" },
      { to: "/office/chat", label: "Team Chat", icon: MessageSquare, testId: "nav-office-chat" },
      { to: "/office/calendar", label: "Calendar & Meetings", icon: CalendarDays, testId: "nav-office-calendar" },
      { to: "/office/crm", label: "CRM & Contacts", icon: Contact, testId: "nav-office-crm" },
      { to: "/office/memos", label: "Corporate Memos", icon: Megaphone, testId: "nav-office-memos" },
    ],
  },


  {
    label: "Administration",

    items: [
      {
        to: "/audit-log",
        label: "Audit Trail (Logs)",
        icon: History,
        testId: "nav-audit",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/reports/controls",
        label: "Accounting Controls",
        icon: ShieldCheck,
        testId: "nav-accounting-controls",
        denyRoles: NO_SALES_DENY,
      },

      {
        to: "/settings",
        label: "Settings",
        icon: Settings,
        testId: "nav-settings",
        roles: [
          "tenant_admin",
          "super_admin",
        ],
      },

      {
        to: "/admin",
        label: "Super Admin",
        icon: ShieldCheck,
        testId: "nav-admin",
        roles: [
          "super_admin",
        ],
      },
    ],
  },
];


export default function Sidebar({
  open,
  onClose,
}) {
  const {
    user,
    tenant,
  } = useAuth();
  const { unreadMailCount = 0 } = useOffice();

  const location = useLocation();


  useEffect(() => {
    if (onClose) {
      onClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  const canSee = (item) => {
    const target = typeof item.to === "string" ? item.to : item.to?.pathname || "/";
    const roleGrants = tenant?.active_role_module_grants || [];
    const isSuperAdmin = user?.role === "super_admin";
    const isDemoRole = user?.assigned_role === "demo";
    const mfiManagerRoute = ["tenant_admin", "mfi_branch_manager"].includes(user?.role) &&
      isMicrofinanceWorkspace(tenant) && (target === "/field" || target.startsWith("/field/") ||
        target === "/microfinance" || target.startsWith("/microfinance/"));
    if (!isSuperAdmin && !isRouteAllowed(target, user?.role, roleGrants, tenant)) return false;
    if (isSuperAdmin && target === "/admin") return true;
    const industry = tenant?.industry?.trim().toLocaleLowerCase() || "";
    const subSector = tenant?.sub_sector?.trim().toLocaleLowerCase() || "";
    if (!isSuperAdmin && !isDemoRole && isMicrofinanceWorkspace(tenant) && target !== "/microfinance" && !target.startsWith("/microfinance/") && target !== "/field") return false;
    const healthcareIndustry = ["healthcare & pharmaceuticals", "hospital & pharmacy", "hospital and pharmacy"].includes(industry);
    const pharmacyWorkspace = subSector.includes("pharmacy") || subSector.includes("pharmacies") || subSector.includes("drugstore") || subSector.includes("drug store") || subSector.includes("chemist");
    const hospitalClinicWorkspace = subSector.includes("hospital") || subSector.includes("clinic");
    if (!isDemoRole && (target === "/hospital" || target.startsWith("/hospital/"))) {
      if (!healthcareIndustry || !["hospital", "clinic"].some((term) => subSector.includes(term))) return false;
    }
    if (!isDemoRole && (target === "/pharmacy" || target.startsWith("/pharmacy/"))) {
      if (!((healthcareIndustry && (pharmacyWorkspace || hospitalClinicWorkspace)) || (industry === "wholesale & retail trade" && pharmacyWorkspace))) return false;
    }
    if (!isDemoRole && (target === "/microfinance" || target.startsWith("/microfinance/") || target === "/field")) {
      const mfiSector = ["microfinance", "savings & loans", "savings and loans", "credit union", "susu"].some((t) => subSector.includes(t));
      if (!((industry.includes("financ") || industry.includes("bank")) && mfiSector)) return false;
    }
    if (!isDemoRole && (target === "/school" || target.startsWith("/school/"))) {
      if (!industry.includes("education") && !industry.includes("school")) return false;
    }
    // Per-tenant module whitelist (Super Admin can restrict via /admin/modules)
    const enabled = tenant?.enabled_modules;
    if (Array.isArray(enabled) && !isDemoRole && !mfiManagerRoute && !enabled.some((module) => (
      target === module || target.startsWith(module + "/")
    ))) {
      return false;
    }
    if (isSuperAdmin) return true;
    return true;
  };


  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed lg:sticky top-0 z-40 h-screen w-[246px] flex-shrink-0 border-r border-border bg-card",
        "transition-transform lg:translate-x-0",
        open
          ? "translate-x-0"
          : "-translate-x-full"
      )}
    >
      {/* ==========================================================
          BRAND HEADER
      ========================================================== */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-border">
        <div className="h-10 w-10 rounded-md overflow-hidden bg-white flex items-center justify-center ring-1 ring-border">
          <img
            src="/branding/egs-logo.jpg"
            alt="EGS Business Suite"
            className="h-full w-full object-contain"
          />
        </div>

        <div className="min-w-0">
          <div className="font-display font-bold text-sm truncate tracking-tight">
            EGS BUSINESS SUITE
          </div>

          <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
            {tenant?.trade_name ||
              tenant?.company_name ||
              "Enterprise"}
          </div>
        </div>
      </div>


      {/* ==========================================================
          NAVIGATION
      ========================================================== */}
      <nav className="px-[5px] py-3 space-y-6 overflow-y-auto h-[calc(100vh-4rem)] pb-8">
        {GROUPS.map((group) => {
          const persistentSubmenuBackground = group.label === "Specialized Modules";
          const visible =
            group.items.filter(canSee);
          const sections = [];
          visible.forEach((item) => {
            let section = sections.find((entry) => entry.label === (item.subcategory || null));
            if (!section) {
              section = { label: item.subcategory || null, items: [] };
              sections.push(section);
            }
            section.items.push(item);
          });

          if (visible.length === 0) {
            return null;
          }

          return (
            <div key={group.label}>
              <div className="mb-2 rounded-md bg-brand/25 px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-obsidian shadow-sm">
                {group.label}
              </div>

              {sections.map((section) => (
                <div key={section.label || "items"} className={section.label ? "mt-2 first:mt-0" : ""}>
                  {section.label && (
                    <div className="mx-2 mb-1 rounded bg-brand/15 px-3 py-1 text-[9px] font-mono font-extrabold uppercase tracking-wider text-foreground">
                      {section.label}
                    </div>
                  )}
                  <ul className={cn("space-y-0.5", section.label && "ml-1 border-l border-border/70 pl-1")}>
                    {section.items.map((item) => {
                  const childItems = Array.isArray(item.children) ? item.children.filter(canSee) : [];
                  const active =
                    location.pathname === item.to ||
                    location.pathname.startsWith(item.to + "/") ||
                    childItems.some((child) => location.pathname === child.to || location.pathname.startsWith(child.to + "/"));

                  const Icon =
                    item.icon;

                  return (
                    <li key={`${item.to || item.label}-${item.label}`}>
                      <Link
                        to={item.to}
                        data-testid={
                          item.testId
                        }
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                          persistentSubmenuBackground && "bg-brand/10 font-semibold uppercase",

                          active ? cn(
                            "bg-foreground/10 text-foreground border-l-2 border-brand pl-[10px]",
                            persistentSubmenuBackground ? "font-semibold" : "font-medium"
                          ) : cn(
                            "text-muted-foreground hover:bg-foreground/5 hover:text-foreground",
                            persistentSubmenuBackground && "font-semibold"
                          )
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" />

                        <span className="truncate">
                          {item.label}
                        </span>
                        {item.to === "/office/mail" && unreadMailCount > 0 && (
                          <span data-testid="nav-office-mail-unread-count" aria-label={`${unreadMailCount} unread messages`} className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[10px] font-semibold text-obsidian">
                            {unreadMailCount > 99 ? "99+" : unreadMailCount}
                          </span>
                        )}
                      </Link>

                        {childItems.length > 0 && (
                          <ul className="ml-2 mt-1 space-y-0.5 border-l border-border/70 pl-1">
                            {childItems.map((child) => {
                              const childTarget = typeof child.to === "string" ? child.to : child.to.pathname;
                              const childSearch = typeof child.to === "string" ? "" : (child.to.search || "");
                              const childActive = location.pathname === childTarget || location.pathname.startsWith(childTarget + "/") || (
                                location.pathname === childTarget && location.search === childSearch
                              );
                              const ChildIcon = child.icon;

                              return (
                                <li key={`${childTarget || child.label}-${child.label}`}>
                                  <Link
                                    to={child.to}
                                    data-testid={child.testId}
                                    className={cn(
                                      "flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors",
                                      childActive
                                        ? "bg-foreground/10 text-foreground font-medium border-l border-brand pl-[9px]"
                                        : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                                    )}
                                  >
                                    {ChildIcon && <ChildIcon className="h-3.5 w-3.5 shrink-0" />}
                                    <span className="truncate">{child.label}</span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                    </li>
                  );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          );
        })}


        {/* ========================================================
            COMPLIANCE
        ======================================================== */}
        <div className="px-3 pt-4 border-t border-border">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Compliance
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border">
              GRA
            </span>

            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border">
              ICAG
            </span>

            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-border">
              IFRS
            </span>
          </div>
        </div>
      </nav>
    </aside>
  );
}
